import React from "react";
import { useNavigate } from "react-router-dom";

import building from "../../../assets/building.webp";
import { exploreSurveyIntentPath } from "../../../configs/ExploreSurveyConfig";
import { pageConfigBySlug } from "../../../services/nav/NavigationService";
import {
  IDashboardBuildingPayload,
  IDashboardTileResult,
} from "../../../services/zeos-api/contracts/Dashboard";
import { dashboardUnitOr } from "../../../services/helpers/DashboardTileHelpers";
import { buildingSavingsHint, buildingSavingsNote } from "../shared/buildingSavingsNote";
import { CoverageDisclosure } from "../shared/CoverageDisclosure";
import { DashboardMetricTile } from "../shared/DashboardMetricTile";

interface IProps {
  hackStoneMill?: boolean | undefined;
  setUnlocked: (unlocked: boolean) => void;
  unlocked: boolean;
  tileResult?: IDashboardTileResult<IDashboardBuildingPayload>;
  loading?: boolean;
  propertyId?: number;
  companyId?: number;
}

export const Building = (props: IProps) => {
  const navigate = useNavigate();
  const exploreSlug = pageConfigBySlug("explore")?.slug || "explore";
  const data = props.tileResult?.data;
  const savingsNote = buildingSavingsNote(data);
  const savingsHint = buildingSavingsHint(data);

  return (
    <DashboardMetricTile
      tileKey="building"
      title="Building Performance"
      image={building}
      navPath={pageConfigBySlug("building")?.slug || "building"}
      unlocked={props.unlocked}
      setUnlocked={props.setUnlocked}
      tileResult={props.tileResult}
      loading={props.loading}
      lockedCopy={{
        bodyHeading: "SAVINGS YOU CAN SEE",
        bodyContent:
          "Get up to the minute information on how your building improvements are performing.",
        onExploreClick: () =>
          navigate(exploreSurveyIntentPath(exploreSlug, "building-performance")),
      }}
      metrics={[
        {
          label: "Consumed",
          value: data?.energyConsumption,
          unit: dashboardUnitOr(data?.energyConsumptionUnit, "kWh"),
          tone: "positive",
        },
        {
          // Usage vs the stored baseline year at delivery rates (bug linked to 4053). Signed: a negative
          // figure means the building used more than in the baseline year, so no abs here.
          label: "Savings Generated",
          value: data?.savingsGenerated,
          unit: dashboardUnitOr(data?.savingsGeneratedUnit, "$"),
          tone: "neutral",
          hint: savingsHint,
        },
      ]}
      // Meter count, caveat and partial-period note (task-4051). Replaces the task-4060 "Consumed · N meters"
      // label; the API sends no coverage for single-meter properties (R1), so they look as before.
      footer={
        <>
          {savingsNote.length > 0 ? (
            // Two short lines at most: the tile body is a fixed 220px. The full wording is the (i) hint.
            <div className="flex flex-col gap-0.5 mt-1 px-4 text-center max-w-md" data-testid="building-savings-note">
              {savingsNote.map((line) => (
                <p key={line} className="m-0 text-xs text-black/70 leading-snug">
                  {line}
                </p>
              ))}
            </div>
          ) : null}
          <CoverageDisclosure compact coverage={data?.coverage} propertyId={props.propertyId} companyId={props.companyId} />
        </>
      }
    />
  );
};
